import React from "react";
import clsx from "clsx";
import { RequiredMarker } from "./TicketRow";

// LQC-2674 - a label plus one control per leg, side by side, so the two legs of a swap
// read as a comparison rather than as two lists. TicketRow's two-column grid cannot do
// this, and a swap has ten of these rows.
//
// Three columns at every width: the widget's minW of 3 (of 12) puts the floor around
// 330px, and Tailwind 3.3 has no container queries, so a width-aware stack is not
// available. The leg columns are minmax(0,1fr) so they shrink and truncate rather than
// overflowing - long values like "EUR-EURIBOR-6M" get a title tooltip from the control.
// The label column must stay identical to TicketRow's, or leg 1 sits off the top
// controls' left edge. Written out in full because Tailwind cannot scan a shared const.
export const LEG_ROW_GRID =
  "grid grid-cols-[minmax(88px,140px)_minmax(0,1fr)_minmax(0,1fr)] items-center gap-2";

interface ITicketLegRow {
  label: string;
  testId: string;
  leg1: React.ReactNode;
  leg2: React.ReactNode;
  // Set per leg while the user hovers a blocked Send and that cell is a reason.
  isLeg1Highlighted?: boolean;
  isLeg2Highlighted?: boolean;
  // Static per row - required on either leg marks the shared label, which is the only
  // place a marker can go.
  isRequired?: boolean;
}

const TicketLegRow = ({
  label,
  testId,
  leg1,
  leg2,
  isLeg1Highlighted = false,
  isLeg2Highlighted = false,
  isRequired = false,
}: ITicketLegRow) => {
  const isHighlighted = isLeg1Highlighted || isLeg2Highlighted;
  return (
    <div
      data-testid={testId}
      id={testId}
      className={clsx(
        LEG_ROW_GRID,
        "border-b py-1 transition-colors",
        isHighlighted
          ? "border-red-500/60 bg-red-500/10"
          : "border-gray-200 dark:border-gray-700",
      )}
    >
      <div
        className={clsx(
          "truncate px-1 text-xs transition-colors",
          isHighlighted
            ? "font-semibold text-red-600 dark:text-red-400"
            : "text-gray-600 dark:text-gray-300",
        )}
        title={label}
      >
        {label}
        <RequiredMarker isRequired={isRequired} isHighlighted={isHighlighted} />
      </div>
      <div className="min-w-0">{leg1}</div>
      <div className="min-w-0">{leg2}</div>
    </div>
  );
};

export default TicketLegRow;
